import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import { Key } from '@phosphor-icons/react';
import styles from './IdentityProviderCard.module.css';
import type { IdentityProvider } from '../identityProviders';
import activeDirectoryLogo from '../logos/Active-Directory.svg';
import auth0Logo from '../logos/Auth0.svg';
import awsLogo from '../logos/AWS.svg';
import azureLogo from '../logos/Azure.svg';
import githubLogo from '../logos/GitHub.svg';
import gitlabLogo from '../logos/GitLab.svg';
import googleLogo from '../logos/Google.svg';
import keycloakLogo from '../logos/Keycloak.svg';
import oktaLogo from '../logos/Okta.svg';
import oneLoginLogo from '../logos/OneLogin.svg';
import pingIdentityLogo from '../logos/Ping-Identity.svg';
import salesforceLogo from '../logos/Salesforce.svg';
import { ExCard } from '@boomi/exosphere';

interface IdentityProviderRowProps {
    idp: IdentityProvider;
    onSelect?: (id: string) => void;
}

interface PhosphorIconConfig {
    icon: PhosphorIcon;
    iconBg: string;
    iconColor: string;
}

const LOGO_MATCHERS: Array<{ keywords: string[]; logoSrc: string }> = [
    { keywords: ['microsoftonline', 'adfs'], logoSrc: activeDirectoryLogo },
    { keywords: ['azure'], logoSrc: azureLogo },
    { keywords: ['accounts.google'], logoSrc: googleLogo },
    { keywords: ['okta.com'], logoSrc: oktaLogo },
    { keywords: ['auth0.com'], logoSrc: auth0Logo },
    { keywords: ['onelogin.com'], logoSrc: oneLoginLogo },
    { keywords: ['pingidentity', 'pingone', 'ping.com'], logoSrc: pingIdentityLogo },
    { keywords: ['keycloak'], logoSrc: keycloakLogo },
    { keywords: ['cognito-idp', 'amazonaws.com'], logoSrc: awsLogo },
    { keywords: ['salesforce.com'], logoSrc: salesforceLogo },
    { keywords: ['gitlab.com'], logoSrc: gitlabLogo },
    { keywords: ['github.com'], logoSrc: githubLogo },
];

function resolveLogoSrc(wellKnownUrl: string): string | undefined {
    const normalized = wellKnownUrl.toLowerCase();
    return LOGO_MATCHERS.find(({ keywords }) => keywords.some((kw) => normalized.includes(kw)))
        ?.logoSrc;
}

const ICON_FALLBACK: PhosphorIconConfig = { icon: Key, iconBg: '#f3f4f6', iconColor: '#374151' };

const IdentityProviderCard = ({ idp, onSelect }: IdentityProviderRowProps) => {
    const { id, name, clientId, wellKnownUrl } = idp;
    const logoSrc = resolveLogoSrc(wellKnownUrl);
    const fallback = ICON_FALLBACK;

    return (
        <ExCard
            id="idp-list-providercard"
            aria-label={name}
            visible={true}
            className={styles.cardIdp}
            onClick={() => onSelect?.(id)}
            data-pendo-card="idp-cardgrid-card"
            data-pendo-idp-name={name}
            data-pendo-idp-id={id}
        >
            <section className={styles.info}>
                <header className={styles.header}>
                    <div
                        className={styles.icon}
                        style={logoSrc ? undefined : { background: fallback.iconBg }}
                    >
                        {logoSrc ? (
                            <img src={logoSrc} width={36} height={36} alt="" aria-hidden />
                        ) : (
                            <fallback.icon size={36} color={fallback.iconColor} weight="fill" />
                        )}
                    </div>
                </header>

                <div className={styles.content}>
                    <span className={styles.name}>{name}</span>
                    <span className={styles.clientIdLabel}>Client ID</span>
                    <span className={styles.clientId}>{clientId}</span>
                </div>
            </section>
        </ExCard>
    );
};

export default IdentityProviderCard;
